import * as THREE from 'three/webgpu';
import { SceneAuxiliaryObjectRegistry } from '../../scene-object';
import { Subject, type Observable, type Subscription } from 'rxjs';
import { markCadObjectAsRuntimeDerived } from '../../document/CadObjectPersistence';
import { CameraEventName, type CameraStateChangedEventPayload } from '../../eventBus';
import { useEvent } from '../../hooks';
import type { Viewer } from '../../viewer/Viewer';
import { PatternDirectionArrowAxisDragController } from './PatternDirectionArrowAxisDragController';
import { computeDirectionArrowWorldSizeFromPixels } from './computeDirectionArrowWorldSizeFromPixels';
import type {
    PatternDirectionArrowAxisDragEvent,
    PatternDirectionArrowAxisDragOptions,
} from './PatternDirectionArrowAxisDragTypes';
import {
    PatternDirectionArrowObject,
    type PatternDirectionArrowVisualInput,
} from './PatternDirectionArrowObject';

/**
 * 方向箭头槽位键。
 * 由调用方自定义，如 'direction1'、'direction2' 等，
 * 便于管理器以槽位为单位独立维护各自的箭头实例。
 */
export type PatternDirectionArrowSlotKey = string;


/**
 * 方向箭头像素尺寸配置。
 */
export interface PatternDirectionArrowPixelSizeInput {
    /** 杆身半径像素尺寸。 */
    shaftRadiusPixels?: number;
    /** 杆身长度像素尺寸。 */
    shaftLengthPixels?: number;
    /** 箭头锥头半径像素尺寸。 */
    headRadiusPixels?: number;
    /** 箭头锥头长度像素尺寸。 */
    headLengthPixels?: number;
    /** 锚点下方保留长度像素尺寸。 */
    bottomLengthPixels?: number;
    /** 锚点上方杆身长度像素尺寸。 */
    topLengthPixels?: number;
    /** 锚点沿方向抬升像素尺寸。 */
    anchorLiftPixels?: number;
    /** 世界尺寸最小值，避免远距离或异常容器下过小。 */
    minWorldSize?: number;
}

/**
 * 方向箭头像素尺寸刷新选项。
 */
export interface PatternDirectionArrowPixelSizeRefreshOptions {
    /**
     * 刷新后是否主动请求渲染。
     *
     * 说明：
     * - 普通业务调用可保持默认 `true`；
     * - 相机帧准备阶段已登记重绘，可传 `false` 避免重复登记；本管理器不直接提交画面。
     */
    requestRender?: boolean;
}


/**
 * 方向箭头设置参数。
 * 在视觉参数基础上增加颜色，供管理器判断是否需要重建箭头实例。
 */
export interface PatternDirectionArrowSetInput extends PatternDirectionArrowVisualInput {
    /** 箭头颜色（十六进制）。 */
    color: number;
    
    /** 轴向拖拽配置；不传时保持不可拖拽。 */
    axisDrag?: PatternDirectionArrowAxisDragOptions;
    
    
    /** 像素尺寸配置；传入后 manager 会基于当前相机换算世界尺寸。 */
    pixelSize?: PatternDirectionArrowPixelSizeInput;
    
}

/**
 * 通用方向箭头管理器。
 *
 * 设计说明：
 * 1. 以槽位键（string）为索引，独立维护每个方向槽的箭头实例；
 * 2. 管理器本身继承 THREE.Group，作为所有箭头对象的统一容器；
 * 3. 通过 `setArrow` 创建或更新箭头，颜色变化时自动销毁旧实例再重建；
 * 4. 通过 `clearArrow` / `clearAll` 从场景中移除并释放资源；
 * 5. `dispose` 全量清理，应在命令取消 / 提交后调用；
 * 6. 管理器只操作 viewer.scene，不持有 UI 层引用，符合 DIP 原则。
 */
export class PatternDirectionArrowManager extends THREE.Group {
    /** Viewer 实例，用于访问场景和触发渲染。 */
    private readonly viewer: Viewer;

    /** CAD 事件总线操作集合。 */
    private readonly eventBus = useEvent();

    /** 相机状态变化订阅；dispose 时必须对称解除。 */
    private readonly cameraStateChangedSubscription: Subscription;

    /**
     * 槽位 → 箭头对象映射表。
     * key 为槽位键，value 为对应的箭头 Group 实例。
     */
    private readonly arrowMap = new Map<PatternDirectionArrowSlotKey, PatternDirectionArrowObject>();

    
    /**
     * 槽位 → 原始箭头输入映射表。
     *
     * 设计说明：
     * 1. 像素尺寸箭头需要在相机缩放、旋转后重新换算世界尺寸；
     * 2. 这里保存调用方传入的原始业务尺寸，刷新时再按最新相机状态解析；
     * 3. 存储克隆值，避免调用方后续修改 Vector3 导致箭头刷新读取到脏数据。
     */
    private readonly arrowInputMap = new Map<PatternDirectionArrowSlotKey, PatternDirectionArrowSetInput>();
    

    
    /** 方向箭头轴向拖拽事件流。 */
    private readonly axisDragSubject = new Subject<PatternDirectionArrowAxisDragEvent>();

    /** 方向箭头轴向拖拽控制器。 */
    private axisDragController: PatternDirectionArrowAxisDragController | null = null;
    

    /**
     * 创建方向箭头管理器。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        super();
        this.viewer = viewer;
        this.name = '__pattern_direction_arrow_manager__';
        // 箭头是由当前命令参数派生的交互资源，其杆身和锥头都不得进入可见模型快照。
        markCadObjectAsRuntimeDerived(this);
        // 箭头及专用轴向手柄不参与组件身份索引，但其交互监听保持不变。
        SceneAuxiliaryObjectRegistry.markRoot(this);
        this.viewer.scene.add(this);
        // resize、投影切换、交互导航和程序化相机更新都汇聚到同一状态事件，
        // 管理器无需反向耦合 CameraManager 的具体调用点。
        this.cameraStateChangedSubscription = this.eventBus.add(
            CameraEventName.StateChanged,
            this.handleCameraStateChangedEvent,
        );
    }

    /**
     * 设置或更新指定槽位的方向箭头。
     *
     * 行为说明：
     * - 若该槽位不存在箭头，创建新实例并加入场景；
     * - 若颜色与现有箭头不同，先销毁旧实例再重建（颜色写入 material，不支持运行时修改）；
     * - 若颜色相同，直接更新位置与方向，避免不必要的 GPU 资源重建。
     *
     * @param slotKey 槽位键。
     * @param input 箭头更新参数（含颜色、位置、方向、尺寸）。
     */
    public setArrow(slotKey: PatternDirectionArrowSlotKey, input: PatternDirectionArrowSetInput): void {
        
        this.ensureAxisDragController();
        

        let arrow = this.arrowMap.get(slotKey);

        // 颜色变化时必须重建，因为颜色已固化到 material 实例中。
        if (arrow && arrow.color !== input.color) {
            this.destroyArrow(slotKey, arrow);
            arrow = undefined;
        }

        // 首次创建时挂入场景。
        if (!arrow) {
            arrow = new PatternDirectionArrowObject(input.color);
            arrow.name = slotKey;
            this.add(arrow);
            this.arrowMap.set(slotKey, arrow);
        }

        
        arrow.setAxisDragOptions(input.axisDrag ?? { enabled: false });
        

        
        // 保存原始输入，让 CameraControls.update 事件可以按最新相机状态刷新像素尺寸箭头。
        this.arrowInputMap.set(slotKey, this.cloneSetInput(input));
        

        // 更新视觉（位置 / 方向 / 尺寸）。
        arrow.updateVisual(this.resolveVisualInput(input));
        this.viewer.requestRender?.();
    }

    /**
     * 清除指定槽位的方向箭头。
     *
     * 从场景中移除后立即调用 `dispose()` 释放 GPU 资源。
     *
     * @param slotKey 槽位键。
     */
    public clearArrow(slotKey: PatternDirectionArrowSlotKey): void {
        const arrow = this.arrowMap.get(slotKey);
        if (!arrow) {
            return;
        }
        this.destroyArrow(slotKey, arrow);
        this.viewer.requestRender?.();
    }

    /**
     * 清除所有槽位的方向箭头。
     * 命令取消或提交后调用，确保场景干净。
     */
    public clearAll(): void {
        // 先收集 key，避免在迭代 Map 时修改导致行为不确定。
        const keys = Array.from(this.arrowMap.keys());
        const hadArrows = keys.length > 0;
        for (const key of keys) {
            const arrow = this.arrowMap.get(key);
            if (arrow) {
                this.destroyArrow(key, arrow);
            }
        }
        if (hadArrows) {
            this.viewer.requestRender?.();
        }
    }

    
    /**
     * 观察方向箭头轴向拖拽事件。
     *
     * @returns 轴向拖拽事件流。
     */
    public observeAxisDrag(): Observable<PatternDirectionArrowAxisDragEvent> {
        return this.axisDragSubject.asObservable();
    }
    

    
    /**
     * 刷新所有启用像素尺寸配置的方向箭头。
     *
     * 设计说明：
     * 1. 此方法由统一的 CameraEventName.StateChanged 事件触发；
     * 2. 不放在渲染循环中主动轮询，避免无相机变化时重复计算；
     * 3. 仅刷新声明了 pixelSize 的箭头，保持旧的世界尺寸箭头不受影响。
     *
     * @param options 刷新选项。
     * @returns 有箭头被刷新时返回 `true`。
     */
    public refreshPixelSizedArrows(options: PatternDirectionArrowPixelSizeRefreshOptions = {}): boolean {
        const viewportHeight = this.viewer.getContainer()?.clientHeight ?? 0;
        if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) {
            // DOM 隐藏或布局切换期间高度可能短暂为 0；跳过本轮即可保留最近一次有效世界尺寸。
            return false;
        }

        let hasRefreshed = false;
        const shouldRequestRender = options.requestRender ?? true;

        for (const [slotKey, input] of this.arrowInputMap) {
            if (!input.pixelSize) {
                continue;
            }

            const arrow = this.arrowMap.get(slotKey);
            if (!arrow) {
                continue;
            }

            // 相机事件发生时只重算视觉尺寸，不改变拖拽配置和槽位生命周期。
            arrow.updateVisual(this.resolveVisualInput(input));
            hasRefreshed = true;
        }

        if (hasRefreshed && shouldRequestRender) {
            this.viewer.requestRender?.();
        }

        return hasRefreshed;
    }
    

    /**
     * 释放管理器本身及所有箭头资源。
     * 生命周期结束时调用（如 Viewer dispose 链）。
     */
    public dispose(): void {
        this.cameraStateChangedSubscription.unsubscribe();
        this.clearAll();
        
        this.axisDragController?.dispose();
        this.axisDragController = null;
        this.axisDragSubject.complete();
        
        if (this.parent) {
            this.parent.remove(this);
        }
    }

    /**
     * 销毁单个箭头：从场景移除 + 释放 GPU 资源 + 从 Map 中删除。
     *
     * @param slotKey 槽位键。
     * @param arrow 箭头实例。
     */
    private destroyArrow(slotKey: PatternDirectionArrowSlotKey, arrow: PatternDirectionArrowObject): void {
        if (arrow.parent) {
            arrow.parent.remove(arrow);
        }
        arrow.dispose();
        this.arrowMap.delete(slotKey);
        
        // 箭头销毁时同步清理原始输入，避免相机事件刷新到已释放对象。
        this.arrowInputMap.delete(slotKey);
        
    }

    
    /**
     * 确保轴向拖拽控制器已绑定。
     */
    private ensureAxisDragController(): void {
        if (!this.axisDragController) {
            this.axisDragController = new PatternDirectionArrowAxisDragController({
                viewer: this.viewer,
                root: this,
                onAxisDrag: (event) => this.axisDragSubject.next(event),
            });
        }
        this.axisDragController.bind();
    }

    /**
     * 根据当前 Viewer 对应的相机状态事件刷新固定像素箭头。
     *
     * @param payload 相机状态事件载荷。
     * @returns 无返回值。
     */
    private readonly handleCameraStateChanged = (payload: CameraStateChangedEventPayload): void => {
        // 全局事件总线可能同时服务多个 Viewer；只消费属于当前 Viewer 活动相机的事件。
        if (payload.camera !== this.viewer.getCamera()) {
            return;
        }
        this.refreshPixelSizedArrows({ requestRender: false });
    };

    /**
     * 处理事件总线的弱类型载荷。
     *
     * @param payload 相机状态事件载荷。
     * @returns 无返回值。
     */
    private readonly handleCameraStateChangedEvent = (payload: unknown): void => {
        this.handleCameraStateChanged(payload as CameraStateChangedEventPayload);
    };
    

    
    /**
     * 解析最终视觉输入。
     *
     * @param input 原始设置参数。
     * @returns 最终视觉参数。
     */
    private resolveVisualInput(input: PatternDirectionArrowSetInput): PatternDirectionArrowVisualInput {
        if (!input.pixelSize) {
            return input;
        }

        const camera = this.viewer.getCamera();
        const container = this.viewer.getContainer();
        const minWorldSize = Math.max(0, input.pixelSize.minWorldSize ?? 1e-6);
        const resolvePixels = (pixelSize: number | undefined, fallbackWorldSize: number): number => {
            if (!Number.isFinite(pixelSize)) {
                return fallbackWorldSize;
            }
            return Math.max(
                minWorldSize,
                computeDirectionArrowWorldSizeFromPixels(camera, container, input.anchorWorld, pixelSize ?? 0),
            );
        };

        // 只覆盖调用方显式声明的像素尺寸，其余尺寸继续沿用原有世界尺寸，保证兼容旧调用。
        return {
            ...input,
            shaftRadius: resolvePixels(input.pixelSize.shaftRadiusPixels, input.shaftRadius),
            shaftLength: resolvePixels(input.pixelSize.shaftLengthPixels, input.shaftLength),
            headRadius: resolvePixels(input.pixelSize.headRadiusPixels, input.headRadius),
            headLength: resolvePixels(input.pixelSize.headLengthPixels, input.headLength),
            bottomLength: input.pixelSize.bottomLengthPixels === undefined
                ? input.bottomLength
                : resolvePixels(input.pixelSize.bottomLengthPixels, input.bottomLength ?? 0),
            topLength: input.pixelSize.topLengthPixels === undefined
                ? input.topLength
                : resolvePixels(input.pixelSize.topLengthPixels, input.topLength ?? input.shaftLength),
            anchorLift: input.pixelSize.anchorLiftPixels === undefined
                ? input.anchorLift
                : resolvePixels(input.pixelSize.anchorLiftPixels, input.anchorLift ?? 0),
        };
    }

    /**
     * 克隆方向箭头设置输入。
     *
     * @param input 原始设置参数。
     * @returns 可安全持有的设置参数副本。
     */
    private cloneSetInput(input: PatternDirectionArrowSetInput): PatternDirectionArrowSetInput {
        return {
            ...input,
            anchorWorld: input.anchorWorld.clone(),
            directionWorld: input.directionWorld.clone(),
            axisDrag: input.axisDrag ? { ...input.axisDrag } : undefined,
            pixelSize: input.pixelSize ? { ...input.pixelSize } : undefined,
        };
    }
    
}
